import { useRecordLastPage } from "@src/hooks/helperHooks";
import {
  useOrganization,
  useOrganizations,
} from "@src/modules/member/hooks/organizations";
import { Organization } from "@src/modules/member/models/organization";
import { DASHBOARD_PATH } from "@src/modules/member/modules/main/router/paths";
import { NEW_ORGANIZATION_PATH } from "@src/modules/member/modules/settings/router/paths";
import {
  buildMemberPath,
  buildMemberSettingsPath,
  memberPathPrefix,
} from "@src/modules/member/path";
import { createContext, useEffect, useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import PortalAccessOverlay from "../../components/portal-access-overlay";
import { LAST_APP_PAGE_VISITED_KEY } from "../../constants";
import { useAuthenticated } from "../../hooks/authorized";
import PermissionContextProvider from "../permission-context";

const LAST_USED_ORG_KEY = "CURRENT_ORGANIZATION_CODE";

export const OrganizationContext = createContext<{
  currentOrganization: Organization | undefined;
  currentOrganizationCode: string;
  isLoading: boolean;
}>({
  currentOrganization: undefined,
  currentOrganizationCode: "",
  isLoading: false,
});

export default function OrganizationContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useAuthenticated();

  const { orgCode } = useParams<{ orgCode: string }>();
  const location = useLocation();
  const currentOrganizationCode = orgCode || "";

  useRecordLastPage(LAST_APP_PAGE_VISITED_KEY, [
    buildMemberSettingsPath(currentOrganizationCode),
  ]);

  const { data: orgsResponse, isLoading: orgsLoading } = useOrganizations();
  const organizations = orgsResponse?.data;

  const { data: currentOrganizationRes } = useOrganization(
    currentOrganizationCode,
  );
  const currentOrganization = currentOrganizationRes?.data;

  useEffect(() => {
    if (currentOrganizationCode) {
      localStorage.setItem(LAST_USED_ORG_KEY, currentOrganizationCode);
    }
  }, [currentOrganizationCode]);

  const contextValue = useMemo(
    () => ({
      currentOrganization,
      currentOrganizationCode,
      isLoading: orgsLoading,
    }),
    [currentOrganization, currentOrganizationCode, orgsLoading],
  );

  if (orgsLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-primary-500" />
      </div>
    );
  }

  if (!organizations || organizations.length === 0) {
    return (
      <Navigate to={`${memberPathPrefix}/${NEW_ORGANIZATION_PATH()}`} replace />
    );
  }

  const orgIsAccessible = organizations.some(
    (org) => org.code === currentOrganizationCode,
  );

  if (!orgIsAccessible) {
    const firstOrg = organizations[0].code;
    const currentOrganizationPrefix = `${memberPathPrefix}/${currentOrganizationCode}`;
    const restOfPath = (
      location.pathname.startsWith(currentOrganizationPrefix)
        ? location.pathname.slice(currentOrganizationPrefix.length)
        : location.pathname
    ).replace(/^\//, "");
    const targetPath = restOfPath
      ? `${buildMemberPath(firstOrg)}/${restOfPath}`
      : `${buildMemberPath(firstOrg)}/${DASHBOARD_PATH()}`;

    return (
      <Navigate
        to={{
          pathname: targetPath,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  return (
    <OrganizationContext.Provider value={contextValue}>
      <PermissionContextProvider>
        <PortalAccessOverlay>{children}</PortalAccessOverlay>
      </PermissionContextProvider>
    </OrganizationContext.Provider>
  );
}
